import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, ControlContainer } from '@angular/forms';
import { ErrorInputFieldComponent, FileException } from '@cargos/sprintpay-models';
import {
    AddressInformationComponent,
    FieldFiles,
    FileData,
    FileEvent,
    FileForm,
    FileHandlerService,
    FileRemoverComponent,
} from '@cargos/sprintpay-ui';
import { Observable, Subject, forkJoin, map, take, takeUntil } from 'rxjs';
import { FileRequestService } from 'src/app/services/file-handler.service';
import Swal from 'sweetalert2';
import { BusinessDetailsFormGroupType } from '../../models/form-types';

@Component({
    selector: 'app-business-details',
    templateUrl: './business-details.component.html',
    styleUrl: './business-details.component.scss',
    standalone: false,
})
export class BusinessDetailsComponent implements OnInit, OnDestroy {
    @ViewChild('addressInformation') addressInformationComponent: AddressInformationComponent;
    @ViewChild('w9FileRemover') w9FileRemover: FileRemoverComponent;
    @ViewChild('bankLetterFileRemover') bankLetterFileRemover: FileRemoverComponent;
    public parentForm: BusinessDetailsFormGroupType;
    public uploading: boolean = false;
    public filesUploaded: FieldFiles[] = [];
    public errorList: ErrorInputFieldComponent[];
    public readonly filesWhiteList: string[] = ['application/pdf'];
    public readonly titleW9: string = 'Upload W9';
    public readonly subtitleW9: string = 'Accepted file type: PDF';
    public readonly titleBankLetter: string = 'Upload Bank Letter or Void Check';
    public readonly subtitleBankLetter: string = 'Accepted file type: PDF';
    private readonly unsubscribe$: Subject<void>;

    constructor(
        private readonly fileRequestService: FileRequestService,
        private readonly fileHandlerService: FileHandlerService,
        private readonly controlContainer: ControlContainer
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.parentForm = this.controlContainer.control as BusinessDetailsFormGroupType;

        this.errorList = [
            {
                code: 'required',
                message: `Required field`,
            },
            {
                code: 'pattern',
                message: `Please enter a valid EIN in the format XX-XXXXXXX.`,
            },
        ];

        this.subscribeFilesUploaded();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    filesAdded(uploadedFiles: FileEvent): void {
        if (this.uploading) {
            return;
        }

        this.uploading = true;

        this.uploadFiles(uploadedFiles.files)
            .pipe(take(1))
            .subscribe({
                next: (locationPaths: { names: string; size: number }[]) => {
                    this.uploading = false;
                    const existingFiles =
                        this.filesUploaded.find((field) => field.files && field.fieldName === uploadedFiles.fieldName)
                            ?.files ?? [];
                    const newLocationPaths: FileData[] = locationPaths;
                    const newFileNames: FileData[] = [...newLocationPaths, ...existingFiles];
                    this.fileHandlerService.setPathFiles({ fieldName: uploadedFiles.fieldName, files: newFileNames });
                    const file = uploadedFiles.files[0].formData.get('file') as File;

                    if (file) {
                        const reader = new FileReader();
                        reader.onload = (e: any) => {
                            const base64String = e.target.result.split(',')[1];
                            const fileDetails = {
                                bodyBase64: base64String,
                                name: file.name,
                                type: file.type,
                            };

                            const jsonString = JSON.stringify(fileDetails);
                            if (uploadedFiles.fieldName === 'w9') {
                                this.parentForm.controls['w9File'].setValue(jsonString);
                            }

                            if (uploadedFiles.fieldName === 'bankLetter') {
                                this.parentForm.controls['bankLetterFile'].setValue(jsonString);
                            }

                            this.uploading = false;
                        };
                        reader.readAsDataURL(file);
                    }
                },
                error: (error: FileException) => {
                    this.uploading = false;
                    if (this.containsHTML(error?.description)) {
                        Swal.fire({
                            title: error.title,
                            html: error?.description,
                            icon: 'warning',
                        });
                        return;
                    }
                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: 'warning',
                    });
                },
            });
    }

    openW9(): void {
        const url = 'https://www.irs.gov/pub/irs-pdf/fw9.pdf';
        window.open(url, '_blank');
    }

    uploadFiles(files: FileForm[]): Observable<{ names: string; size: number }[]> {
        return forkJoin(
            files.map((file) =>
                this.fileRequestService.uploadFile(file.formData).pipe(
                    take(1),
                    map((locationPath) => ({
                        names: locationPath,
                        size: file.size,
                    }))
                )
            )
        );
    }

    subscribeFilesUploaded(): void {
        this.fileHandlerService
            .getAllPathFiles()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (filesUploaded) => {
                    this.filesUploaded = filesUploaded;

                    if (filesUploaded.find((file) => file.fieldName === 'w9')?.files?.length === 0) {
                        this.w9Control?.setValue('');
                    }

                    if (filesUploaded.find((file) => file.fieldName === 'bankLetter')?.files?.length === 0) {
                        this.bankLetterControl?.setValue('');
                    }
                },
            });
    }

    containsHTML(str: string): boolean {
        const htmlRegex: RegExp = /<\/?[a-z][\s\S]*>/i;
        return htmlRegex.test(str);
    }

    get bankLetterControl(): AbstractControl<string> | null {
        return this.parentForm.get('bankLetterFile');
    }

    get w9Control(): AbstractControl<string> | null {
        return this.parentForm.get('w9File');
    }
}
